import { ProductDocument } from '@util/types/firestore/products';
import { cva, VariantProps } from 'class-variance-authority';
import dynamic from 'next/dynamic';
import SafeImage from '@ui/SafeImage';
import Link from 'next/link';
import { useProductCard } from '@util/hooks/useProductCard';
import { formatCurrency } from '@util/index';
import { GavelIcon } from '@c/products/AuctionBanner';
import useCountdown from '@util/hooks/useCountdown';
import { useQuery } from '@tanstack/react-query';
import { getHighestBid } from '@util/firestore/bid/bid.service';
// Import directly from master.service, not the @util/firestore/master
// barrel — the barrel also re-exports master.types, an 18.8 KB module
// whose value-level `MasterDocumentSchema = z.object({...})` export
// webpack can't tree-shake (no sideEffects:false in package.json). Every
// other @util/firestore/master import in a broadly-shipped component is
// type-only and gets elided; this would have been the first value import,
// shipping that schema to the homepage/wishlist/PDP/search bundles too.
import { getMasterById } from '@util/firestore/master/master.service';
import { getProductUrl } from '@util/urlHelpers';
import ProductSchema from '@c/schema/ProductSchema';
import { memo, useMemo, useState, useEffect } from 'react';
import { formatProductItems, trackEvent } from '@util/analytics';
import { useListId } from '@util/hooks/useListId';
import { getConditionShortLabel } from '@util/conditions/conditionShortLabel';
import { useEffectivePrice } from 'context/StoreSaleContext';

const HeartIconButton = dynamic(() => import('@c/icons/buttons/HeartButton'), {
  ssr: false,
});

const imageStyles = cva(
  'rounded-[10px] flex justify-center items-center overflow-clip',
  {
    variants: {
      variant: {
        xSmall: 'h-[14.4rem] w-[14.4rem]',
        small: 'h-[14.4rem] w-[14.4rem] sm:h-[21.2rem] sm:w-[21.2rem]',
        normal:
          'min-h-[14.4rem] sm:min-h-[28.8rem] min-w-[14.4rem] sm:min-w-[28.8rem]',
      },
    },
    defaultVariants: {
      variant: 'normal',
    },
  }
);

interface ProductCardProps extends VariantProps<typeof imageStyles> {
  item: ProductDocument;
  newTab?: boolean;
  variant?: 'normal' | 'small' | 'xSmall';
  noHover?: boolean;
  coverImage?: boolean;
  onHeartButtonClick?: (liked: boolean) => void;
  priority?: boolean;
  /** When true, show orange market badge instead of red sale badge (D&S page only) */
  showMarketBadge?: boolean;
  /**
   * When true, resolve and show a "See Full Specs & Reviews" link to this
   * listing's matched GearMaster page (/m/[slug]), if one exists. Defaults
   * to false: ProductCard is shared across 8+ surfaces (category pages,
   * homepage, wishlist, PDP related products, and GearMaster pages
   * themselves via AllListingsCarousel), and turning this on everywhere
   * would render self-referencing links on /m/ pages (a card for a listing
   * already matched to the master page it's shown on). Only real category
   * listing grids opt in — CategoryOrSubcategoryPage gates this on having
   * an actual category (it's also rendered by the BFCM landing page with
   * no category, which must not get this) (GB web#1005).
   */
  showMasterLink?: boolean;
}

// Memoized function to format shipping alert
const ShippingAlert = memo(({ shipping }: { shipping: any }) => {
  if (!shipping) return null;
  
  let lowestCost = Infinity;
  shipping.forEach((item: any) => {
    if (item.cost < lowestCost) {
      lowestCost = item.cost;
    }
  });

  if (lowestCost === 0) {
    return <p className="text-[1.1rem] leading-[2.4rem]">🚚 Free Shipping</p>;
  }
  
  return null;
});

ShippingAlert.displayName = 'ShippingAlert';

// Memoized auction timer component
const AuctionTimer = memo(({
  startTime,
  endTime,
  timeRemaining,
  isEnded,
  isStarting,
}: {
  startTime: number,
  endTime: number,
  timeRemaining: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  isEnded: boolean;
  isStarting: boolean;
}) => {
  // Don't render if auction has ended
  if (isEnded) {
    return null;
  }

  return (
    <div className="absolute bottom-0 left-0 z-20 flex h-[5rem] w-full items-center justify-center gap-[0.8rem] rounded-b-[10px] bg-gradient-orange text-white">
      <GavelIcon />
      <span className="w-[15rem] whitespace-nowrap text-[1.4rem] font-medium">
        {isStarting ? 'Starts in' : 'Ends in'}{' '}
        {timeRemaining.days > 0 && `${timeRemaining.days}d `}
        {timeRemaining.hours}h {timeRemaining.minutes}m{' '}
        {timeRemaining.seconds}s
      </span>
    </div>
  );
});

AuctionTimer.displayName = 'AuctionTimer';

// Memoized auction sold component
const AuctionSold = memo(({ highestBidPrice }: { highestBidPrice: number }) => (
  <div className="absolute bottom-0 left-0 z-20 flex h-[5rem] w-full items-center justify-center gap-[0.8rem] rounded-b-[10px] bg-green-500 text-white">
    <GavelIcon />
    <span className="w-[15rem] whitespace-nowrap text-[1.8rem] font-medium">
      Sold for {formatCurrency(highestBidPrice)}!
    </span>
  </div>
));

AuctionSold.displayName = 'AuctionSold';

const ProductCard = ({
  item,
  newTab,
  variant = 'normal',
  coverImage = false,
  priority = false,
  showMarketBadge = false,
  showMasterLink = false,
}: ProductCardProps) => {
  const { getSalePercent } = useProductCard(item);
  const storePrice = useEffectivePrice(item);
  const storeSale = storePrice?.applied;
  const displayPrice = storeSale ? storePrice.unitCents / 100 : item.price;

  // Hydration-safe: track if component has mounted on client
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Calculate auction status only after mount to avoid hydration mismatch.
  // Never compare a real timestamp against an epoch-0 sentinel: both flags are
  // false until mounted (Date.now() runs only then), so pre-mount (SSR + first
  // client render) the auction overlays below stay in their neutral state and
  // no cached HTML commits to a wrong "starting"/"ended" state.
  const isAuctionStarting =
    mounted && !!item.start_time && item.start_time > Date.now();
  const isAuctionEnded =
    mounted && !!item.end_time && item.end_time < Date.now();

  // Determine the type of countdown based on the auction status
  const countdownType = isAuctionStarting ? 'start' : 'end';
  // Call useCountdown at the top level
  const timeRemaining = useCountdown(item.start_time!, item.end_time!, countdownType);

  // Fetch highest bid data for auctions
  const { data: highestBid } = useQuery(
    ['highestBid', item.id],
    () => getHighestBid(item.id),
    {
      enabled: item.is_auction,
      refetchInterval: 10000,
    }
  );

  // Resolve this listing's matched GearMaster model, if any, for a /m/ back-link.
  // Keyed by master ID (not item.id) so cards sharing the same model reuse one
  // cached fetch instead of each issuing its own.
  //
  // Legacy products may store gearMaster as a bare string rather than an
  // array (same shape products/[slug].tsx already normalizes for) — indexing
  // a string with [0] returns its first CHARACTER, not the ID, silently
  // sending a broken lookup (Codex finding, GB web#1005 gate round 2).
  const gearMasterArray = item.gearMaster
    ? Array.isArray(item.gearMaster)
      ? item.gearMaster
      : [item.gearMaster]
    : [];
  const matchedMasterId = gearMasterArray[0];
  const { data: matchedMaster } = useQuery(
    ['productCardMaster', matchedMasterId],
    () => getMasterById(matchedMasterId),
    {
      enabled: showMasterLink && !!matchedMasterId,
      staleTime: 60 * 60 * 1000,
    }
  );

  // Memoize computed values to prevent recalculations
  const bidPrice = useMemo(() => 
    highestBid?.bid_price ?? item.price,
    [highestBid?.bid_price, item.price]
  );
  
  const productUrl = useMemo(() => 
    getProductUrl(item.slug),
    [item.slug]
  );

  // get item list id based on the current route
  const listId = useListId()  

  
  // Track select_item event when a product is clicked
  const handleProductClick = () => {
    trackEvent("select_item", {
      item_list_id: listId,
      item_list_name: listId,
      items: formatProductItems([item], listId),
    });
  }

  // Track clicks on the /m/ back-link, same event as the main listing
  // click — EcommerceEventType is a shared GA4/Meta CAPI vocabulary, not
  // something to extend for one UI affordance. Without this the link's
  // usage would be entirely unmeasured (GB web#1005 gate finding).
  const handleMasterLinkClick = handleProductClick;

  return (
    <div className="flex flex-col items-center">
        <div className="relative">
          {item.is_auction && mounted && !isAuctionEnded && (
            <AuctionTimer
              startTime={item.start_time!}
              endTime={item.end_time!}
              timeRemaining={timeRemaining}
              isEnded={isAuctionEnded}
              isStarting={isAuctionStarting}
            />
          )}

          {item.is_auction && mounted && isAuctionEnded && (
            <AuctionSold highestBidPrice={bidPrice} />
          )}
          
          {showMarketBadge && (item as any).marketDiscountPercent >= 5 && !item.out_of_stock ? (
            (item as any).marketSavings > 75 ? (
              <div className="absolute left-[0.4rem] top-[1.2rem] z-10 flex flex-col items-center justify-center rounded-full bg-[#059669] px-[0.8rem] py-[0.4rem] text-white drop-shadow-md sm:left-[1.6rem] sm:top-[1.6rem] sm:px-[1rem]">
                <span className="text-[1.1rem] font-medium leading-tight sm:text-[1.3rem]">
                  {(item as any).marketDiscountPercent}% Below Market
                </span>
                <span className="text-[1.1rem] font-extrabold leading-tight sm:text-[1.3rem]">
                  Save ~${Math.round((item as any).marketSavings).toLocaleString()}
                </span>
              </div>
            ) : (
              <div className="absolute left-[0.4rem] top-[1.2rem] z-10 flex h-[3rem] w-auto items-center justify-center rounded-full bg-[#059669] px-[0.8rem] text-[1.2rem] font-extrabold text-white drop-shadow-md sm:left-[1.6rem] sm:top-[1.6rem] sm:h-[3.2rem] sm:px-[1rem] sm:text-[1.4rem]">
                Save {(item as any).marketDiscountPercent}% vs Market
              </div>
            )
          ) : storeSale && !item.out_of_stock && !showMarketBadge ? (
          <div className="absolute left-[0.4rem] top-[1.2rem] z-10 flex h-[3rem] w-[7rem] items-center justify-center rounded-full bg-red-500 text-[1.3rem] font-bold text-white sm:left-[1.6rem] sm:top-[1.6rem] sm:h-[3.2rem] sm:w-[9.4rem] sm:px-[1rem] sm:py-[2rem]">
              {storeSale.percentOff}% Off
            </div>
          ) : item.on_sale && !item.out_of_stock && getSalePercent() >= 5 && !showMarketBadge ? (
          <div className="absolute left-[0.4rem] top-[1.2rem] z-10 flex h-[3rem] w-[7rem] items-center justify-center rounded-full bg-red-500 text-[1.3rem] font-bold text-white sm:left-[1.6rem] sm:top-[1.6rem] sm:h-[3.2rem] sm:w-[9.4rem] sm:px-[1rem] sm:py-[2rem]">
              {getSalePercent()}% Off
            </div>
          ) : null}
          
          {item.out_of_stock && !item.is_auction && (
            <Link
              target={newTab ? '_blank' : '_self'}
              href={productUrl}
            >
              <div className="absolute left-1/2 top-1/2 z-10 flex h-[3.2rem] w-[9.4rem] -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full bg-red-600 px-[1.2rem] py-[2rem] text-[1.3rem] text-base font-bold text-white">
                Sold Out
              </div>
              {/* overlay */}
              <div className="absolute left-0 top-0 h-full w-full rounded-[10px] bg-brand-black/60" />
            </Link>
          )}
          
          <div className="absolute right-4 top-4 z-20 cursor-pointer">
            <HeartIconButton
              itemId={item.id}
              initialCount={item.favorite_count}
            />
          </div>
          
          <Link
            className={imageStyles({ variant })}
            target={newTab ? '_blank' : '_self'}
            href={productUrl}
            onClick={handleProductClick}
          >
            <SafeImage
              title={`View ${item.title}`}
              alt={item.title}
              src={item.thumbnail}
              height={288}
              width={288}
              className={coverImage ? 'object-cover w-full h-full hover:scale-105' : 'hover:scale-105'}
              loading={priority ? 'eager' : 'lazy'}
              priority={priority}
            />
          </Link>
        </div>
        <div
          className={`mt-[1.6rem] flex h-full w-full flex-col justify-start gap-[0.2rem]`}
        >
          {item.bumps > 0 && !showMarketBadge && (
              <p className="text-[1.3rem] font-semibold text-brand-primary">GearFocus Bump</p>
            )}
          <Link
            href={productUrl}
            title={`View ${item.title} on GearFocus`}
            target={newTab ? '_blank' : '_self'}
            className="line-clamp-2 min-h-[3rem] text-[1.8rem] font-normal leading-[2.4rem]"
            onClick={handleProductClick}
          >
            <span>{item.title}</span>
          </Link>

          <div className="self-baseline">
            {item.condition && (
              <p className="text-[1.1rem] leading-[2.4rem]">{getConditionShortLabel(item.condition)}</p>
            )}

            {storeSale && !item.out_of_stock ? (
              <div className="gap-[0.8rem] sm:flex-row">
                <p className="text-[1.4rem] leading-[2.4rem] text-gray-600 line-through">{formatCurrency(item.price)}</p>
                <p className="text-[1.8rem] font-bold leading-[2.4rem] text-brand-secondary">{formatCurrency(displayPrice)}</p>
              </div>
            ) : item.on_sale && !item.out_of_stock ? (
              <div className="gap-[0.8rem] sm:flex-row">
                <p className="text-[1.4rem] font-normal leading-[2.4rem] text-gray-600">
                  <span className="line-through">{formatCurrency(item.og_price)}</span>
                  <span className="block md:inline text-brand-red text-[1.2rem] font-medium md:pl-[0.5rem]">
                    ${Math.round(Number(item.og_price - item.price))} price drop
                  </span>
                </p>
                <p className="text-[1.8rem] font-bold leading-[2.4rem] text-brand-secondary">
                  {formatCurrency(item.price)}
                </p>
              </div>
            ) : item.is_auction ? (
              <p className="text-[1.8rem] font-bold leading-[2.4rem] text-brand-secondary">
                {formatCurrency(bidPrice)}
              </p>
            ) : (
              <p className="text-[1.8rem] font-bold leading-[2.4rem] text-brand-secondary">
                {formatCurrency(item.price)}
              </p>
            )}

            {((item.integrations && item.integrations.source && item.integrations.source !== 'refocus') || (item.external_source && item.external_source !== 'refocus')) && (
              <p className="text-[1.1rem] leading-[2.4rem]">🤝 Trusted Partner</p>
            )}
            
            <ShippingAlert shipping={item.shipping_costs} />

            {showMasterLink && matchedMasterId && (
              // Reserve the line's height as soon as a lookup is attempted
              // (not only once it resolves) so the card doesn't grow after
              // paint and shift the rest of the grid — the async resolution
              // is invisible to layout even though it's still async to data.
              <div className="min-h-[2.8rem]">
                {matchedMaster?.slug && (
                  <Link
                    href={`/m/${matchedMaster.slug}`}
                    target={newTab ? '_blank' : '_self'}
                    onClick={handleMasterLinkClick}
                    aria-label={`See full specs and reviews for ${item.title}`}
                    className="text-[1.2rem] leading-[2.8rem] underline hover:text-brand-primary"
                  >
                    See Full Specs &amp; Reviews
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
        <ProductSchema product={item} pricing={storePrice ?? undefined} />
    </div>
  );
};

export default memo(ProductCard);
